//Octo10 FormPublishContext:
// Updated Mar29 - FormPublishContext with decoupled screenshot logic
import React, { createContext, useContext, useMemo } from 'react';
import { useFormContext } from './FormContext';
import { useAuth } from './AuthContext';
import { useFormTheme } from './FormThemeContext';
import { useFormLayout } from './FormLayoutContext';
import { useDropdownDependencies } from './DropdownDependenciesContext';
import { useFormSubmissionSettings } from './FormSubmissionSettingsContext';

// canonical in-memory stores (avoid legacy storage reads where possible)
import { useFieldMetadata } from './FieldMetadataContext';
import { useMultiAnswerField } from './MultiAnswerFieldContext';
import { useFormListsDraftSync } from './FormListsDraftSyncContext';

import {
  cleanDropdownDependenciesForOptions,
} from '../utils/formListsDependencyCleanupUtils';

import { auth, db, app } from '../firebase';

import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

import { prepareFormDataForFirestore } from '../utils/formDataUtils';
import {
  checkFormExistence,
  savePublishedFormToFirestore,
} from '../utils/firestore';

import { safeGetItem, safeRemoveItem } from '../utils/storageUtils';
import {
  publishStagedFormIntegrationConfigs,
} from '../utils/formIntegrationConfigSync';

import { generateAndUploadScreenshot } from '../utils/screenshot';
import { v4 as uuidv4 } from 'uuid';
import { resolveAssetVersionInfo } from '../utils/assetVersioning';
 import { buildAssetFilterCatalogHttp } from
   '../utils/buildAssetFilterCatalogHttp';
import {
  ONBOARDING_EVENTS,
  ONBOARDING_EVENT_SOURCES,
} from '../utils/onboardingLifecycle';

const FormPublishContext = createContext();

  export const FormPublishProvider = ({
    children,
    initialAssetIngestConfig = null,
  }) => {
  const { formId, formDetails, formTitle, formDescription } = useFormContext();
  const { organizationId, userPermissions, getToken, hasPermission } = useAuth();
  const { colors, fonts, submitButtonStyle, fieldVariant, selectedTheme } = useFormTheme();
  const { layoutConfig } = useFormLayout();
  const { dropdownDependencies = {} } = useDropdownDependencies() || {};
  const { submissionSettings } = useFormSubmissionSettings();

  // canonical in-memory stores (avoid legacy storage reads)
  const { fieldsMetadata: fieldsMetadataFromCtx } = useFieldMetadata() || {};
  const { dropdownOptions: dropdownOptionsFromCtx } = useMultiAnswerField() || {};
  const {
    flushFormListsDraftChanges,
    clearFormListsUnsavedWorkingCopy,
  } = useFormListsDraftSync() || {};

  // Match FormLayoutContext policy for choosing builder view mode
  const isBuilderRoute = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.location.pathname.startsWith('/form-builder');
  }, []);

  const builderViewMode = useMemo(() => {
    try {
      if (!formId) return 'auto';
      const raw = sessionStorage.getItem(`form_${formId}_layoutVersionMode`);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.value || 'auto'; // 'auto' | 'published' | 'draftOverlay'
    } catch (e) {
      return 'auto';
    }
  }, [formId]);

  const effectiveViewMode = useMemo(() => {
    // Only builder honors the toggle; everything else is published.
    if (isBuilderRoute) return builderViewMode;
    return 'published';
  }, [isBuilderRoute, builderViewMode]);

  /**
   * Records form-publish progress after the published Firestore commit
   * succeeds.
   *
   * Enterprise rules:
   * - Do not block publish UX if lifecycle recording fails.
   * - Do not mark activation here; backend reducer owns state transition.
   * - Publishing moves lifecycle readiness forward, but activation remains
   *   behavior-based through successful campaign URL submission.
   * - Use a per-publish eventId so the audit trail can capture republish
   *   events while the backend preserves firstPublishedAt.
   */
  const recordPublishOnboardingEvent = async ({
    currentUser,
    existingStatus = 'none',
    publishResponse = null,
    isAssetUpload = false,
    replaceExisting = false,
  } = {}) => {
    if (!currentUser?.uid) {
      console.log(
        '[FormPublishContext] Skipping publish onboarding event because ' +
        'no authenticated user is present.'
      );
      return null;
    }

    if (!formId) {
      console.warn(
        '[FormPublishContext] Skipping publish onboarding event because ' +
        'formId is missing.'
      );
      return null;
    }

    try {
      const functionsInstance = getFunctions(app, 'us-central1');
      const recordOnboardingEvent = httpsCallable(
        functionsInstance,
        'recordOnboardingEvent'
      );

      const eventId =
        `form_publish_${currentUser.uid}_${formId}_${Date.now()}`;

      const response = await recordOnboardingEvent({
        eventName: ONBOARDING_EVENTS.CAMPAIGN_URL_FORM_PUBLISHED,
        eventId,
        insertId: eventId,
        eventSource: ONBOARDING_EVENT_SOURCES.FORM_PUBLISH_CONTEXT,
        formId,
        eventProperties: {
          formId,
        },
        metadata: {
          source: 'FormPublishContext',
          existingStatus,
          effectiveViewMode,
          publishStatus: publishResponse?.status || null,
          publishMessage: publishResponse?.message || null,
          isAssetUploadForm: isAssetUpload === true,
          replaceExisting: replaceExisting === true,
        },
      });

      console.log('[FormPublishContext] Onboarding publish event recorded:', {
        eventId,
        result: response?.data || null,
      });

      return response?.data || null;
    } catch (eventErr) {
      console.warn(
        '[FormPublishContext] Onboarding publish event failed (non-fatal):',
        {
          formId,
          error: eventErr?.message || eventErr,
        }
      );

      return null;
    }
  };


  const publishForm = async ({
    replaceExisting = false,
    assetIngestConfig: assetIngestConfigProp = initialAssetIngestConfig,
  } = {}) => {
    console.log('[FormPublishContext] Starting form publication process...');

    const USE_ASYNC_SCREENSHOT = true;

    const themeLastUpdated = new Date().toISOString();


    const currentUser = auth.currentUser;

    if (!currentUser) {
      console.error('[FormPublishContext] No authenticated user found. Cannot publish the form.');
      return;
    }

    if (!organizationId) {
      console.warn('[FormPublishContext] Warning: organizationId is missing or empty.');
    } else {
      console.log('[FormPublishContext] organizationId from useAuth:', organizationId);
    }

    if (!formDetails) {
      console.error('[FormPublishContext] formDetails is undefined! Please check useFormContext.');
      return;
    }

    if (!formTitle || !formDescription) {
      console.warn('[FormPublishContext] Warning: formTitle or formDescription is missing before constructing updatedFormDetails.');
    }

   // ──────────────────────────────────────────────────────────
   // STEP A: Fetch a single ID token for the entire publish flow
   // Reuse this token for every backend call in this run to avoid
   // redundant auth calls (screenshot, AUF publish, catalog, etc.)
   // ──────────────────────────────────────────────────────────
   const idToken = await getToken();

   if (!idToken) {
     console.error('[FormPublishContext] No ID token; aborting publish.');
     return;
   }

   const enqueueScreenshot = async (previewTokenArg = null) => {
     try {
       // Enterprise policy: caller must pass the previewToken that was
       // committed (or will be committed) for THIS publish run.
       // This keeps enqueue deterministic and prevents drift caused by
       // stale outer-state reads (formDetails/updatedFormDetails).
       const previewToken =
         typeof previewTokenArg === "string" &&
         previewTokenArg.trim() !== "" ?
           previewTokenArg.trim() :
           null;

       if (!previewToken) {
         console.warn(
           "[FormPublishContext] enqueue skipped: previewToken missing",
           { formId, organizationId },
         );
         return;
       }

       const res = await fetch(
         "https://us-central1-myutms-f8cba.cloudfunctions.net/" +
           "enqueueFormScreenshot",
         {
           method: "POST",
           headers: {
             Authorization: `Bearer ${idToken}`,
             "Content-Type": "application/json",
           },
           body: JSON.stringify({ formId, organizationId, previewToken }),
         },
       );

       if (!res.ok) {
         const txt = await res.text().catch(() => "");
         console.warn(
           "[FormPublishContext] enqueue failed:",
           res.status,
           txt,
         );
         return;
       }

       console.log("[FormPublishContext] Screenshot job enqueued.");
     } catch (e) {
       console.warn(
         "[FormPublishContext] enqueue error:",
         e?.message || e,
       );
     }
   };


    // ──────────────────────────────────────────────────────────
    // 0) Vanity URL lifecycle policy
    // ──────────────────────────────────────────────────────────
    // Vanity URL saves, edits, deletes, and registry repairs are now
    // owned by the setFormVanitySlug callable. Publish preserves the
    // committed form vanity metadata but does not mutate the registry.
    //
    // Keep this legacy key only as cleanup so stale localStorage from
    // older builds cannot trigger future vanity URL changes.
    const pendingKey = `pendingVanity_${formId}`;


//  Step 1: Check Firestore for existing draft or published version
    let existingStatus = 'none';
    try {
      const existence = await checkFormExistence(formId);
      existingStatus = existence?.status ?? 'none';
      console.log(`[FormPublishContext] Form Status in Firestore: ${existingStatus}`);
    } catch (error) {
      console.error('[FormPublishContext] Error checking Firestore for existing form:', error);
      return;
    }

    // ────────────────────────────────────────────────────────────────
    // Read latest committed vanity metadata from Firestore.
    // This prevents publish from using stale FormContext vanity data after
    // a vanity URL was saved from Form Settings.
    // ────────────────────────────────────────────────────────────────
    let committedVanitySlug = formDetails?.vanitySlug || null;
    let committedVanityUrl = formDetails?.vanityUrl || null;

    try {
      const latestFormSnap = await getDoc(doc(db, 'forms', formId));

      if (latestFormSnap.exists()) {
        const latestFormData = latestFormSnap.data() || {};
        committedVanitySlug = latestFormData.vanitySlug || null;
        committedVanityUrl = latestFormData.vanityUrl || null;

        console.log('[FormPublishContext] Latest vanity metadata:', {
          formId,
          committedVanitySlug,
          committedVanityUrl,
        });
      }
    } catch (vanityReadErr) {
      console.warn(
        '[FormPublishContext] Could not read latest vanity ' +
          'metadata; preserving context value:',
        vanityReadErr
      );
    }

    // ────────────────────────────────────────────────────────────────
    // Sanitize base formDetails so we never re-write blobs/overlay keys
    // into metadata. This matches FormSaveContext’s separation:
    //   • metadata (title, access, ownership, etc.)
    //   • blobs (compressedData/layoutConfig/theme/etc.)
    //   • working copy (draftOverlay)
    // ────────────────────────────────────────────────────────────────
    const {
      draftOverlay: _draftOverlay,
      draftOverlayStatus: _draftOverlayStatus,
      draftOverlaySavedAt: _draftOverlaySavedAt,
      draftOverlaySavedBy: _draftOverlaySavedBy,

      compressedData: _compressedDataBlob,
      layoutConfig: _layoutConfigBlob,
      theme: _themeBlob,
      fieldsMetadata: _fieldsMetadataBlob,
      dropdownOptions: _dropdownOptionsBlob,
      dropdownDependencies: _dropdownDependenciesBlob,
      submissionSettings: _submissionSettingsBlob,

      ...baseFormDetails
    } = formDetails || {};

    // Step 2: Construct updated formDetails
    const updatedFormDetails = {
      ...baseFormDetails,
      title: formTitle,
      description: formDescription,
      createdBy: baseFormDetails?.createdBy || currentUser.uid,
      owner: baseFormDetails?.owner || currentUser.uid,
      organizationId: organizationId || '',
      isTemplate       : baseFormDetails?.isTemplate,      // ← no default
      templateScope    : baseFormDetails?.templateScope,
      templateCategory : baseFormDetails?.templateCategory,
      isPublic: baseFormDetails?.isPublic || false,
      orgAccess    : baseFormDetails?.orgAccess   ?? 'none',   // NEW keep/seed
      sharedWith   : baseFormDetails?.sharedWith  ?? {},       // NEW keep/seed
      lastModified: new Date(),
      previewToken: baseFormDetails?.previewToken || uuidv4(),
      vanitySlug: committedVanitySlug || null,
      vanityUrl: committedVanityUrl || null,
    };



    /* ────────────────────────────────────────────────────────────
       If the in-memory cache lost the template flags (e.g. hot reload
       or Builder opened from a cold start), re-read them from the
       current Firestore doc so we don’t wipe them on publish.
       ────────────────────────────────────────────────────────────*/
    if (updatedFormDetails.isTemplate == null) {
      try {
        const snap = await getDoc(doc(db, 'forms', formId));
        if (snap.exists() && snap.data()?.isTemplate) {
          const data = snap.data();
          updatedFormDetails.isTemplate       = true;
          updatedFormDetails.templateScope    = data.templateScope    ?? updatedFormDetails.templateScope;
          updatedFormDetails.templateCategory = data.templateCategory ?? updatedFormDetails.templateCategory;
          updatedFormDetails.isPublic         = data.isPublic         ?? updatedFormDetails.isPublic;
          updatedFormDetails.orgAccess        = data.orgAccess        ?? updatedFormDetails.orgAccess;
          updatedFormDetails.sharedWith       = data.sharedWith       ?? updatedFormDetails.sharedWith;
          console.log('[FormPublishContext] Re-hydrated template flags from Firestore:', {
            isTemplate       : updatedFormDetails.isTemplate,
            templateScope    : updatedFormDetails.templateScope,
            templateCategory : updatedFormDetails.templateCategory,
          });
        }
      } catch (rehydrateErr) {
        console.warn('[FormPublishContext] Could not re-read template flags:', rehydrateErr);
      }
    }
    console.log(`[FormPublishContext] Preview token for form ${formId}:`, updatedFormDetails.previewToken);
    console.log('[FormPublishContext] Updated Form Details (with isPublic & isTemplate):', updatedFormDetails);

    // ─────────────────────────────────────────────────────────────
    // Flush pending Form Lists V2 spreadsheet edits before publish
    // reads dropdown options for screenshot/session data and compression.
    //
    // WHY
    //  • FormListsLab keeps hot edits local for spreadsheet speed.
    //  • Publish must capture those edits deterministically.
    //  • The flush returns a dropdownOptions override so this publish run does
    //    not depend on React context timing after setDropdownOptions(...).
    //
    // IMPORTANT
    //  • This is NOT a full-canvas flush.
    //  • The registered FormListsLab flusher builds a dirty-only apply plan.
    // ─────────────────────────────────────────────────────────────
    let formListsDropdownOptionsOverride = null;

    try {
      if (typeof flushFormListsDraftChanges === 'function') {
        const formListsFlushResult = await flushFormListsDraftChanges({
          formId,
          reason: 'publish',
        });

        formListsDropdownOptionsOverride =
          formListsFlushResult?.dropdownOptionsOverride || null;

        console.log('[FormPublishContext] Form Lists V2 flush before publish:', {
          status: formListsFlushResult?.status || null,
          reason: formListsFlushResult?.reason || null,
          hasDropdownOptionsOverride:
            !!formListsDropdownOptionsOverride &&
            Object.keys(formListsDropdownOptionsOverride || {}).length > 0,
          resultCount: Array.isArray(formListsFlushResult?.results)
            ? formListsFlushResult.results.length
            : 0,
        });
      }
    } catch (flushError) {
      console.error('[FormPublishContext] Failed to flush Form Lists V2 edits before publish:', flushError);
      return;
    }

   // ─────────────────────────────────────────────────────────────
   // Load per-form ingest mapping (form-scoped). If the builder UI
   // already has this in memory, you can prefer that here.
   // ─────────────────────────────────────────────────────────────
   let assetIngestConfig = null;
   try {
     const snap = await getDoc(doc(db, 'forms', formId));
     if (snap.exists()) {
       assetIngestConfig = snap.data()?.assetIngestConfig || null;
     }
   } catch (e) {
     console.warn('[FormPublishContext] Could not load assetIngestConfig:',
       e);
   }

    // ──────────────────────────────────────────────────────────────
    // Make a *root-level* object that preserves template flags. This
    // will be merged into the payload we write to Firestore so those
    // flags never get wiped by sub-object compression.
    // ───────────────────────────────────────────────────────────────
    const rootTemplateMeta = {};
//    if (updatedFormDetails?.isTemplate) {
    if (updatedFormDetails.isTemplate) {            // 🆕 always a boolean now
      rootTemplateMeta.isTemplate = true;
      if (updatedFormDetails.templateScope)
        rootTemplateMeta.templateScope = updatedFormDetails.templateScope;
      if (updatedFormDetails.templateCategory)
        rootTemplateMeta.templateCategory = updatedFormDetails.templateCategory;
      if (updatedFormDetails.isPublic !== undefined)
        rootTemplateMeta.isPublic = updatedFormDetails.isPublic;
      if (updatedFormDetails.orgAccess !== undefined)
        rootTemplateMeta.orgAccess = updatedFormDetails.orgAccess;
      if (updatedFormDetails.sharedWith !== undefined)
        rootTemplateMeta.sharedWith = updatedFormDetails.sharedWith;
    }

    // Check template permissions if user is trying to publish a template
    if (updatedFormDetails.isTemplate) {
      // If it's a global template (isPublic = true)
      if (updatedFormDetails.isPublic) {
        if (!hasPermission('publishGlobalTemplates')) {
          console.error('[FormPublishContext] Current user lacks permission to publish a GLOBAL template.');
          return; // Abort publishing
        }
      } 
      // Otherwise, treat as an organization-level template
      else {
        if (!hasPermission('publishOrganizationTemplates')) {
          console.error('[FormPublishContext] Current user lacks permission to publish an ORG template.');
          return; // Abort publishing
        }
      }
    }
    // END NEW PERMISSION CHECK

    // 2.5) Vanity URL registry lifecycle
    // ──────────
    // No-op by design.
    //
    // Enterprise policy:
    // The publish flow must not claim, delete, swap, defer, or repair
    // vanity URL mappings. That lifecycle is owned by setFormVanitySlug
    // so forms/{formId} and vanityUrls/{slug} remain consistent in one
    // backend transaction.

    //  Step 3: Trigger screenshot logic with versioning
    if (!USE_ASYNC_SCREENSHOT) {

    try {
      console.log('[FormPublishContext] Resolving screenshot version info...');
      let versionInfo = { versionNumber: 1, previousAssetId: null };

      try {
        versionInfo = await resolveAssetVersionInfo({
          assetType: 'screenshot',
          organizationId,
          formId,
          status: 'published',
        });
        console.log('[FormPublishContext] Resolved screenshot version info:', versionInfo);
      } catch (versionErr) {
        console.warn('[FormPublishContext] Failed to resolve screenshot version info:', versionErr);
      }

      const previewToken = updatedFormDetails.previewToken;
     const authToken = idToken;

      const tsKey             = `form_${formId}_themeLastUpdated`; // legacy key name for preview session
      const tsKeyMode         = `form_${formId}_themeLastUpdated__${effectiveViewMode}`;
      const tsKeyPublished    = `form_${formId}_themeLastUpdated__published`;

      const localThemeUpdated =
        safeGetItem(tsKeyMode) ||
        safeGetItem(tsKeyPublished) ||
        safeGetItem(tsKey) ||
        new Date().toISOString();

      // Mode-aware caches (fallback chain is important)
      const layoutModeKey = `form_${formId}_layoutConfig__${effectiveViewMode}`;
      const layoutPubKey  = `form_${formId}_layoutConfig__published`;

      const colorsModeKey = `form_${formId}_colors__${effectiveViewMode}`;
      const colorsPubKey  = `form_${formId}_colors__published`;

      const fontsModeKey  = `form_${formId}_fonts__${effectiveViewMode}`;
      const fontsPubKey   = `form_${formId}_fonts__published`;

      const btnModeKey    = `form_${formId}_submitButtonStyle__${effectiveViewMode}`;
      const btnPubKey     = `form_${formId}_submitButtonStyle__published`;

      const variantModeKey = `form_${formId}_fieldVariant__${effectiveViewMode}`;
      const variantPubKey  = `form_${formId}_fieldVariant__published`;

      const selThemeModeKey = `form_${formId}_selectedTheme__${effectiveViewMode}`;
      const selThemePubKey  = `form_${formId}_selectedTheme__published`;

      const metaModeKey   = `form_${formId}_fieldsMetadata__${effectiveViewMode}`;
      const metaPubKey    = `form_${formId}_fieldsMetadata__published`;

      const depsModeKey   = `form_${formId}_dropdownDependencies__${effectiveViewMode}`;
      const depsPubKey    = `form_${formId}_dropdownDependencies__published`;

      const sessionLayout =
        safeGetItem(layoutModeKey) ||
        safeGetItem(layoutPubKey) ||
        safeGetItem(`form_${formId}_layoutConfig`) ||
        layoutConfig ||
        {};

      const sessionColors =
        safeGetItem(colorsModeKey) ||
        safeGetItem(colorsPubKey) ||
        safeGetItem(`form_${formId}_colors`) ||
        colors ||
        {};

      const sessionFonts =
        safeGetItem(fontsModeKey) ||
        safeGetItem(fontsPubKey) ||
        safeGetItem(`form_${formId}_fonts`) ||
        fonts ||
        {};

      const sessionBtn =
        safeGetItem(btnModeKey) ||
        safeGetItem(btnPubKey) ||
        safeGetItem(`form_${formId}_submitButtonStyle`) ||
        submitButtonStyle ||
        {};

      const sessionVariant =
        safeGetItem(variantModeKey) ||
        safeGetItem(variantPubKey) ||
        safeGetItem(`form_${formId}_fieldVariant`) ||
        fieldVariant ||
        'outlined';

      const sessionSelectedTheme =
        safeGetItem(selThemeModeKey) ||
        safeGetItem(selThemePubKey) ||
        safeGetItem(`form_${formId}_selectedTheme`) ||
        selectedTheme ||
        'Paper';

      const sessionFieldsMetadata =
        (fieldsMetadataFromCtx && typeof fieldsMetadataFromCtx === 'object' && Object.keys(fieldsMetadataFromCtx).length > 0)
          ? fieldsMetadataFromCtx
          : (
              safeGetItem(metaModeKey) ||
              safeGetItem(metaPubKey) ||
              safeGetItem(`form_${formId}_fieldsMetadata`) ||
              {}
            );

      const sessionDropdownOptions =
        formListsDropdownOptionsOverride &&
        typeof formListsDropdownOptionsOverride === 'object' &&
        Object.keys(formListsDropdownOptionsOverride).length > 0
          ? formListsDropdownOptionsOverride
          : (
              (dropdownOptionsFromCtx && typeof dropdownOptionsFromCtx === 'object' && Object.keys(dropdownOptionsFromCtx).length > 0)
                ? dropdownOptionsFromCtx
                : (safeGetItem(`form_${formId}_dropdownOptions`) || {})
            );

      const sessionDropdownDependencies =
        safeGetItem(depsModeKey) ||
        safeGetItem(depsPubKey) ||
        safeGetItem(`form_${formId}_dropdownDependencies`) ||
        dropdownDependencies ||
        {};

      // NEW: Mode-aware submission settings keys (aligned with FormSubmissionSettingsContext)
      // NOTE: Screenshot preview still expects the legacy key name inside sessionData,
      // but the VALUE must come from the correct mode-aware store.
      const submModeKey    = `submissionSettings_${formId}__${effectiveViewMode}`;
      const submPubKey     = `submissionSettings_${formId}__published`;
      const submLegacyKey  = `submissionSettings_${formId}`;

      // Prefer in-memory if it's "real" (has any enabled action), else mode-aware storage.
      const hasEnabledAction =
        submissionSettings &&
        typeof submissionSettings === 'object' &&
        Object.values(submissionSettings).some((a) => a && a.enabled === true);

      const sessionSubmissionSettings =
        (hasEnabledAction ? submissionSettings : null) ||
        safeGetItem(submModeKey) ||
        safeGetItem(submPubKey) ||
        safeGetItem(submLegacyKey) ||
        submissionSettings ||
        {};

      console.log('[FormPublishContext] Screenshot session submissionSettings source check:', {
        effectiveViewMode,
        hasEnabledAction,
        hitModeKey: !!safeGetItem(submModeKey),
        hitPubKey: !!safeGetItem(submPubKey),
        hitLegacyKey: !!safeGetItem(submLegacyKey),
        enabledAction:
          (sessionSubmissionSettings &&
            typeof sessionSubmissionSettings === 'object' &&
            Object.entries(sessionSubmissionSettings).find(([, v]) => v?.enabled)?.[0]) ||
          null,
      });


      const sessionData = {
        // IMPORTANT: keep LEGACY key names here because the screenshot preview
        // bootstraps from these expected keys.
        [`form_${formId}_layoutConfig`]: sessionLayout,
        [`form_${formId}_colors`]: sessionColors,
        [`form_${formId}_fonts`]: sessionFonts,
        [`form_${formId}_submitButtonStyle`]: sessionBtn,
        [`form_${formId}_fieldVariant`]: sessionVariant,
        [`form_${formId}_selectedTheme`]: sessionSelectedTheme,
        [`form_${formId}_fieldsMetadata`]: sessionFieldsMetadata,
        [`form_${formId}_dropdownOptions`]: sessionDropdownOptions,
        [`form_${formId}_dropdownDependencies`]: sessionDropdownDependencies,

        // Screenshot preview expects this legacy key name:
        [`submissionSettings_${formId}`]: sessionSubmissionSettings,

        [`form_${formId}_formDetails`]: updatedFormDetails,
        [`form_${formId}_title`]: formTitle,
        [`form_${formId}_description`]: formDescription,
        [tsKey]: localThemeUpdated,
        isPublicPreview: true,
      };



//      console.log('[FormPublishContext] Injected title/description session keys into sessionData:', {
//        [`form_${formId}_title`]: sessionData[`form_${formId}_title`],
//        [`form_${formId}_description`]: sessionData[`form_${formId}_description`],
 //     });


      const screenshotAsset = await generateAndUploadScreenshot({
        formId,
        previewToken,
        sessionData,
        organizationId,
        userId: currentUser.uid,
        userPermissions,
        authToken,
        screenshotStatus: "published",
        additionalMetadata: {
          formId,
          status: "published",
          versionNumber: versionInfo.versionNumber,
          previousAssetId: versionInfo.previousAssetId,
          isLatestVersion: true,
        },
      });



      if (screenshotAsset) {
        const originalPath  = screenshotAsset.versions?.original?.path   || null;
        const thumbnailPath = screenshotAsset.versions?.thumbnail?.path  || null;

        // store paths (NOT expiring URLs) on the form record
        if (originalPath)  updatedFormDetails.screenshotPath       = originalPath;
        if (thumbnailPath) updatedFormDetails.screenshotThumbnail  = thumbnailPath;

        console.log('[FormPublishContext] 🔗 Screenshot paths attached to formDetails:', {
          screenshotPath      : updatedFormDetails.screenshotPath,
          screenshotThumbnail : updatedFormDetails.screenshotThumbnail,
        });
      } else {
        console.warn('[FormPublishContext] Screenshot service returned nothing.');
      }


    } catch (screenshotError) {
      console.error('[FormPublishContext] Screenshot logic failed:', screenshotError);
    }
    } //close wrapper for inline screenshot block

  //  Step 3: Prepare form data for Firestore (still needed for saving)
  let preparedFormData;

  // HOIST: make these available to the whole prepare block
  let fm = null;
  let dd = null;
  let lc = null;


  console.log(
    '[FormPublishContext] dropdownDependencies before prepare:',
    JSON.stringify(dropdownDependencies, null, 2)
  );

  // ─────────────────────────────────────────────────────────────
  // Ensure prepareFormDataForFirestore reads the same “active mode”
  // data the builder is using right now.
  // This is NOT changing your schema; it just ensures any internal
  // storage reads see the correct values for this publish run.
  // ─────────────────────────────────────────────────────────────
  try {
    const metaModeKey = `form_${formId}_fieldsMetadata__${effectiveViewMode}`;
    const metaPubKey  = `form_${formId}_fieldsMetadata__published`;

    const depsModeKey = `form_${formId}_dropdownDependencies__${effectiveViewMode}`;
    const depsPubKey  = `form_${formId}_dropdownDependencies__published`;

    const layoutModeKey = `form_${formId}_layoutConfig__${effectiveViewMode}`;
    const layoutPubKey  = `form_${formId}_layoutConfig__published`;

    // ✅ assign to the outer variables (NOT const)
    fm =
      (fieldsMetadataFromCtx && typeof fieldsMetadataFromCtx === 'object' && Object.keys(fieldsMetadataFromCtx).length > 0)
        ? fieldsMetadataFromCtx
        : (safeGetItem(metaModeKey) || safeGetItem(metaPubKey) || safeGetItem(`form_${formId}_fieldsMetadata`) || {});

    dd =
      safeGetItem(depsModeKey) ||
      safeGetItem(depsPubKey) ||
      safeGetItem(`form_${formId}_dropdownDependencies`) ||
      dropdownDependencies ||
      {};

    lc =
      safeGetItem(layoutModeKey) ||
      safeGetItem(layoutPubKey) ||
      safeGetItem(`form_${formId}_layoutConfig`) ||
      layoutConfig ||
      {};

    console.log('[FormPublishContext] Mode-aware snapshot chosen for prepare:', {
      effectiveViewMode,
      fieldsMetadataKeys: Object.keys(fm || {}).length,
      dropdownDepKeys: Object.keys(dd || {}).length,
      layoutRows: Object.keys(lc || {}).length,
    });
  } catch (e) {
    console.warn('[FormPublishContext] Failed to build mode-aware snapshot for prepare (non-fatal):', e?.message || e);
  }


    try {
      // ─────────────────────────────────────────────────────────────
      // Mode-aware source of truth for publish:
      // Use the snapshot we just resolved for THIS effectiveViewMode.
      // This avoids relying on legacy storage keys AND avoids using
      // stale in-memory context when the builder mode is not 'published'.
      // ─────────────────────────────────────────────────────────────
      const layoutForPrepare = lc || layoutConfig || {};
      const rawDepsForPrepare = dd || dropdownDependencies || {};
      const metaForPrepare   = fm || {};

      // Theme: prefer FormThemeContext (canonical). If it’s not hydrated,
      // fall back to mode-aware cached keys like we already do for screenshot.
      const themeSelectedForPrepare =
        safeGetItem(`form_${formId}_selectedTheme__${effectiveViewMode}`) ||
        safeGetItem(`form_${formId}_selectedTheme__published`) ||
        safeGetItem(`form_${formId}_selectedTheme`) ||
        selectedTheme ||
        'Paper';

      const themeColorsForPrepare =
        safeGetItem(`form_${formId}_colors__${effectiveViewMode}`) ||
        safeGetItem(`form_${formId}_colors__published`) ||
        safeGetItem(`form_${formId}_colors`) ||
        colors ||
        {};

      const themeFontsForPrepare =
        safeGetItem(`form_${formId}_fonts__${effectiveViewMode}`) ||
        safeGetItem(`form_${formId}_fonts__published`) ||
        safeGetItem(`form_${formId}_fonts`) ||
        fonts ||
        {};

      const themeBtnForPrepare =
        safeGetItem(`form_${formId}_submitButtonStyle__${effectiveViewMode}`) ||
        safeGetItem(`form_${formId}_submitButtonStyle__published`) ||
        safeGetItem(`form_${formId}_submitButtonStyle`) ||
        submitButtonStyle ||
        {};

      const themeVariantForPrepare =
        safeGetItem(`form_${formId}_fieldVariant__${effectiveViewMode}`) ||
        safeGetItem(`form_${formId}_fieldVariant__published`) ||
        safeGetItem(`form_${formId}_fieldVariant`) ||
        fieldVariant ||
        'outlined';

      // Submission settings (mode-aware, aligned with FormSubmissionSettingsContext)
      const submModeKey    = `submissionSettings_${formId}__${effectiveViewMode}`;
      const submPubKey     = `submissionSettings_${formId}__published`;
      const submLegacyKey  = `submissionSettings_${formId}`;

      // Prefer in-memory only if it actually represents a chosen action
      const hasEnabledAction =
        submissionSettings &&
        typeof submissionSettings === 'object' &&
        Object.values(submissionSettings).some((a) => a && a.enabled === true);

      const submissionSettingsForPrepare =
        (hasEnabledAction ? submissionSettings : null) ||
        safeGetItem(submModeKey) ||
        safeGetItem(submPubKey) ||
        safeGetItem(submLegacyKey) ||
        submissionSettings ||
        {};


      // Dropdown options:
      //  1. Prefer Form Lists V2 flush override from this publish run.
      //  2. Then prefer in-memory MultiAnswerFieldContext.
      //  3. Then fallback to storage.
      const dropdownOptionsForPrepare =
        formListsDropdownOptionsOverride &&
        typeof formListsDropdownOptionsOverride === 'object' &&
        Object.keys(formListsDropdownOptionsOverride).length > 0
          ? formListsDropdownOptionsOverride
          : (
              (dropdownOptionsFromCtx && typeof dropdownOptionsFromCtx === 'object' && Object.keys(dropdownOptionsFromCtx).length > 0)
                ? dropdownOptionsFromCtx
                : (safeGetItem(`form_${formId}_dropdownOptions`) || {})
            );

      /**
       * Clean dropdown dependencies at the Publish boundary.
       *
       * WHY
       *  • Spreadsheet clearing is local and reversible until Save/Publish.
       *  • DropdownDependenciesContext no longer performs destructive cleanup
       *    during normal dropdownOptions changes.
       *  • Publish is an intentional persistence boundary, so this is the
       *    correct time to remove dependencies for options that are no longer
       *    present in the dropdownOptions snapshot being published.
       *
       * IMPORTANT
       *  • This does NOT change the dependency schema.
       *  • It complements the existing formDataUtils prune safety net.
       *  • It ensures screenshot/session/prepare all work from a deterministic
       *    cleaned dependency snapshot for this publish run.
       */
      const {
        dropdownDependencies: depsForPrepare,
        diagnostics: dropdownDependencyCleanupDiagnostics,
      } = cleanDropdownDependenciesForOptions({
        dropdownOptions: dropdownOptionsForPrepare,
        dropdownDependencies: rawDepsForPrepare,
        preserveEmptyDependencyEntries: true,
      });

      console.log('[FormPublishContext] Cleaned dropdown dependencies for publish:', {
        cleanupDiagnostics: dropdownDependencyCleanupDiagnostics,
        rawDependencySetCount: Object.keys(rawDepsForPrepare || {}).length,
        cleanedDependencySetCount: Object.keys(depsForPrepare || {}).length,
      });


      // IMPORTANT: If prepareFormDataForFirestore currently reads from storage internally,
      // it should still be correct because we are passing explicit objects for layout/deps/settings/theme.
      // The only remaining internal read would be fieldsMetadata/dropdownOptions; we can pass them
      // once the util supports it as parameters. For now we keep a debug snapshot.
      console.log('[FormPublishContext] prepare inputs snapshot (mode-aware):', {
        effectiveViewMode,
        layoutRows: Object.keys(layoutForPrepare || {}).length,
        depSets: Object.keys(depsForPrepare || {}).length,
        metaKeys: Object.keys(metaForPrepare || {}).length,
        dropdownSets: Object.keys(dropdownOptionsForPrepare || {}).length,
      });

      preparedFormData = await prepareFormDataForFirestore({
        formId,
        formDetails: updatedFormDetails,
        formTitle,
        formDescription,

        layoutConfig: layoutForPrepare,
        dropdownDependencies: depsForPrepare,
        submissionSettings: submissionSettingsForPrepare,

        selectedTheme: themeSelectedForPrepare,
        colors: themeColorsForPrepare,
        fonts: themeFontsForPrepare,
        submitButtonStyle: themeBtnForPrepare,
        fieldVariant: themeVariantForPrepare,

        organizationId,
        userId: currentUser.uid,
        status: 'published',

        // tells formDataUtils which mode-aware keys apply
        viewMode: effectiveViewMode,

        // eliminates the remaining storage reads during this publish run
        fieldsMetadataOverride: metaForPrepare,
        dropdownOptionsOverride: dropdownOptionsForPrepare,
        submissionSettingsOverride: submissionSettingsForPrepare,

        // keep your existing props (extra keys are harmless if formDataUtils ignores them)
        themeLastUpdated,
      });


      if (!preparedFormData) {
        console.error(
          '[FormPublishContext] ERROR: Failed to prepare form data ' +
          'for Firestore.'
        );
        return;
      }

      console.log(
        '[FormPublishContext] Prepared Form Data:',
        preparedFormData
      );
    } catch (error) {
      console.error(
        '[FormPublishContext] Error during ' +
          'prepareFormDataForFirestore:',
        error
      );
      return;
    }

// Step 4: Save final form to Firestore
try {
  if (existingStatus === 'published') {
    console.log(
      '[FormPublishContext] Overwriting existing published form ' +
        '(creating a backup)...'
    );
  } else if (existingStatus === 'draft') {
    console.log(
      '[FormPublishContext] Publishing existing draft as a ' +
        'published form...'
    );
  } else {
    console.log(
      '[FormPublishContext] No existing form found in Firestore, ' +
        'creating a new published form...'
    );
  }

  // ────────────────────────────────────────────────────────────
  // Branch: Asset Upload Form vs Normal Form
  //
  // ENTERPRISE POLICY (Step 1):
  // Publish MUST be fast and consistent. We always commit the published
  // blobs via savePublishedFormToFirestore (same as regular forms).
  //
  // AUF uniqueness (one per org) + entitlement checks are enforced
  // server-side (functions layer). The client should not block the
  // publish UX on a long-running HTTP endpoint.
  // ────────────────────────────────────────────────────────────
  const isAssetUpload =
    updatedFormDetails?.isAssetUploadForm === true ||
    Boolean(assetIngestConfig);

  // Prefer mapping passed by the UI; else use what we loaded
  const effectiveIngest =
    assetIngestConfigProp || assetIngestConfig || null;

  // Keep a unified shape for the shared post-success block
  let publishResponse = { status: 'success', message: '' };

  // Build one payload for ALL forms; then layer AUF invariants if needed.
  const publishPayload = {
    ...preparedFormData,
    ...rootTemplateMeta,
    ...(isAssetUpload
      ? {
          // AUF invariants (must remain org-wide, not public)
          isAssetUploadForm: true,
          isPublic: false,
          orgAccess: 'view',

          // Persist ingest mapping when available
          ...(effectiveIngest ? { assetIngestConfig: effectiveIngest } : {}),
        }
      : {}),
  };

  publishResponse = await savePublishedFormToFirestore(
    publishPayload,
    existingStatus,
    /* merge = */ true
  );

  if (isAssetUpload) {
    console.log(
      '[FormPublishContext] AUF published via Firestore path (fast).',
      {
        formId,
        organizationId,
        hasAssetIngestConfig: Boolean(effectiveIngest),
        replaceExisting, // kept for UI flow; server enforces singleton
      }
    );
  }

  // ────────────────────────────────────────────────────────────
  // Shared post-success block (runs for both branches)
  // ────────────────────────────────────────────────────────────
  if (publishResponse.status === 'success') {
    console.log(
      `[FormPublishContext] Form ${formId} published successfully.`
    );

    try {
      const integrationPublishResult =
        await publishStagedFormIntegrationConfigs({
          formId,
          organizationId,
          viewMode: effectiveViewMode,
        });

      console.log(
        '[FormPublishContext] Staged integration configs publish result:',
        integrationPublishResult
      );
    } catch (integrationPublishError) {
      console.warn(
        '[FormPublishContext] Staged integration config publish failed ' +
          '(non-fatal):',
        integrationPublishError
      );
    }

    try {
      if (typeof clearFormListsUnsavedWorkingCopy === 'function') {
        clearFormListsUnsavedWorkingCopy({
          formId,
          reason: 'publish_success',
        });
      }
    } catch (formListsDirtyClearError) {
      console.warn(
        '[FormPublishContext] Failed to clear Form Lists dirty state ' +
          'after successful publish (non-fatal):',
        formListsDirtyClearError
      );
    }

    await recordPublishOnboardingEvent({
      currentUser,
      existingStatus,
      publishResponse,
      isAssetUpload,
      replaceExisting,
    });

    // Option 1: enqueue screenshot job (async worker generates it)
    if (USE_ASYNC_SCREENSHOT) {
      await enqueueScreenshot(updatedFormDetails?.previewToken || null);
    }

    // Legacy cleanup only. Vanity URL changes are committed through
    // Form Settings via setFormVanitySlug, not during publish.
    safeRemoveItem(pendingKey);

    // DAM-lite: set the org pointer so Asset Studio/uploader can find it
    try {
      if (isAssetUpload && organizationId) {
        await updateDoc(doc(db, 'organizations', organizationId), {
          assetUploadFormId: formId,
          assetUploadFormPublishedAt: serverTimestamp(),
        });
        console.log(
          '[FormPublishContext] Set organizations/%s.assetUploadFormId = %s',
          organizationId,
          formId
        );

        // ──────────────────────────────────────────────────────────
        // NEW: persist AUF blobs so Library & Catalog see latest data
        // We only write the two compressed blobs we prepared.
        // Do this BEFORE building the org's assetFilterCatalog.
        // ──────────────────────────────────────────────────────────
try {
  const draftB64  = preparedFormData?.compressedDraftData || null;
  const layoutB64 = preparedFormData?.compressedLayoutConfig || null;
  const themeB64  = preparedFormData?.compressedThemeData || null;
  const fieldsB64 = preparedFormData?.compressedFieldsMetadata || null;
  const optsB64   = preparedFormData?.compressedDropdownOptions || null;
  const depsB64   = preparedFormData?.compressedDropdownDependencies || null;
  const submB64   = preparedFormData?.compressedSubmissionSettings || null;

  console.log('[PUBLISH:AUF] prepared blobs', {
    draftLen : (draftB64  || '').length,
    layoutLen: (layoutB64 || '').length,
    themeLen : (themeB64  || '').length,
    fieldsLen: (fieldsB64 || '').length,
    optsLen  : (optsB64   || '').length,
    depsLen  : (depsB64   || '').length,
    submLen  : (submB64   || '').length,
  });

  await updateDoc(doc(db, 'forms', formId), {
    ...(draftB64  ? { compressedData       : draftB64  } : {}),
    ...(layoutB64 ? { layoutConfig         : layoutB64 } : {}),
    ...(themeB64  ? { theme                : themeB64  } : {}),
    ...(fieldsB64 ? { fieldsMetadata       : fieldsB64 } : {}),
    ...(optsB64   ? { dropdownOptions      : optsB64   } : {}),
    ...(depsB64   ? { dropdownDependencies : depsB64   } : {}),
    ...(submB64   ? { submissionSettings   : submB64   } : {}),
    status               : 'published',      // belt & suspenders
    isAssetUploadForm    : true,
    lastModified         : serverTimestamp(),
    lastModifiedBy       : currentUser.uid,
  });
  console.log('[FormPublishContext] AUF blobs saved (full set).');
} catch (e) {
  console.warn('[FormPublishContext] AUF: failed writing full blobs (non-fatal):', e?.message || e);
}


        // Optional: clear client caches to avoid stale first paint
        try {
          // label and choice maps are per-org keys we used for fast paint
          // (no-op if your storageUtils is already clearing elsewhere)
          safeRemoveItem?.(`org_${organizationId}_aufLabelMap`);
          safeRemoveItem?.(`org_${organizationId}_aufChoicesMap`);
          console.log(
            '[FormPublishContext] Cleared AUF label/choices caches ' +
            'for org %s',
            organizationId
          );
        } catch (e) {
          console.warn(
            '[FormPublishContext] Cache clear failed (non-fatal):',
            e?.message || e
          );
        }

// Build/refresh the org's Asset Filter Catalog (HTTP + CORS, like others)
try {
  const region = 'us-central1';
  const projectId =
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    process.env.NX_FIREBASE_PROJECT_ID ||
    process.env.VITE_FIREBASE_PROJECT_ID ||
    (auth?.app?.options?.projectId ?? '');

  const payload = { organizationId, aufId: formId };
  console.log(
    '[FormPublishContext] buildAssetFilterCatalogHttp → calling via ' +
      'HTTPS onRequest (CORS) with payload %o',
    payload
  );

  const resp = await buildAssetFilterCatalogHttp({
    organizationId,
    aufId: formId,
    authToken: idToken,  // reuse the one token from Step A
    region,
    projectId,
  });

  console.log(
    '[FormPublishContext] buildAssetFilterCatalogHttp ok (resp=%o)',
    resp
  );
} catch (e) {
  console.warn(
    '[FormPublishContext] buildAssetFilterCatalogHttp failed (non-fatal):',
    e?.message || e
  );
}


      }
    } catch (e) {
      console.warn(
        '[FormPublishContext] Could not set org assetUploadFormId pointer:',
        e
      );
    }
  } else {
    console.error(
      `[FormPublishContext] Failed to publish form ${formId}:`,
      publishResponse.message
    );
  }
} catch (error) {
  console.error(
    `[FormPublishContext] Unexpected error while publishing form ` +
      `${formId}:`,
    error
  );
}

  };


  return (
    <FormPublishContext.Provider value={{ publishForm }}>
      {children}
    </FormPublishContext.Provider>
  );
};

export const useFormPublishContext = () => useContext(FormPublishContext);
